import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Database,
  ChartLine,
  File,
  VideoCamera,
  Table,
  Gear,
  ArrowRight,
  Newspaper,
  ChatCircle,
  GridFour,
  Brain,
  Gauge
} from "@phosphor-icons/react";

const SharedDataSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const inputs = [
    { icon: Database, label: "Systems" },
    { icon: ChartLine, label: "Reports" },
    { icon: File, label: "Documents" },
    { icon: VideoCamera, label: "Meetings" },
    { icon: Gear, label: "Workflows" },
    { icon: Table, label: "Prior Decisions" },
  ];

  const outputs = [
    { icon: Newspaper, label: "AI-powered Briefs" },
    { icon: ChatCircle, label: "Risks" },
    { icon: GridFour, label: "Actions" },
    { icon: Brain, label: "Follow-up Questions" },
    { icon: Gauge, label: "AI Readiness View" },
  ];

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      data-testid="shared-data-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0B]" />

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="10" name="Context Layer" />
          <span className="mono-label">AI Context Layer</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            The enterprise context layer behind <span className="gradient-text">OnTenacity AI</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg">
            OnTenacity brings systems, reports, documents, meetings, and prior decisions into one 
            shared enterprise context layer — giving AI the grounding it needs to produce stronger 
            briefs, risks, actions, and follow-up questions for leadership.
          </p>
        </motion.div>

        {/* Architecture Diagram */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
          data-testid="architecture-diagram"
        >
          {/* Architecture Image */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-8">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/b99a1bd2-14ed-4a29-ab32-1d20e8c6eee5/images/03b9b1d462ac47d8575a98782e5027a3e343648e5bc9ad540b25d04708098946.png"
              alt="OnTenacity Architecture"
              className="w-full h-auto opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
          </div>

          {/* Flow Visualization */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {/* Inputs */}
            <div className="space-y-3" data-testid="architecture-inputs">
              <h4 className="text-xs font-mono text-[#00E5FF] uppercase tracking-wider mb-4">
                Inputs
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {inputs.map((input, i) => (
                  <motion.div
                    key={input.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.05 }}
                    className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/5"
                  >
                    <input.icon size={16} className="text-[#A1A1AA]" />
                    <span className="text-xs text-[#FAFAFA]">{input.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Core */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col items-center"
              data-testid="architecture-core"
            >
              <div className="w-full flex items-center justify-center gap-2 mb-4">
                <ArrowRight size={16} className="text-[#00E5FF] hidden md:block" />
                <div className="md:hidden h-6 w-px bg-gradient-to-b from-[#00E5FF] to-transparent" />
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#00E5FF]/20 to-[#06B6D4]/20 rounded-2xl blur-xl" />
                <div className="relative px-6 py-4 rounded-xl border-2 border-[#00E5FF]/30 bg-[#0F0F10]">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#06B6D4] flex items-center justify-center">
                      <Database size={24} className="text-[#050505]" />
                    </div>
                    <h4 className="text-sm font-semibold text-white font-['Outfit']">
                      Shared Context Layer
                    </h4>
                    <p className="text-xs text-[#A1A1AA] mt-1">
                      Common Intelligence Fabric
                    </p>
                  </div>
                </div>
              </div>

              <div className="w-full flex items-center justify-center gap-2 mt-4">
                <ArrowRight size={16} className="text-[#06B6D4] hidden md:block" />
                <div className="md:hidden h-6 w-px bg-gradient-to-b from-transparent to-[#06B6D4]" />
              </div>
            </motion.div>

            {/* Outputs */}
            <div className="space-y-3" data-testid="architecture-outputs">
              <h4 className="text-xs font-mono text-[#06B6D4] uppercase tracking-wider mb-4">
                Outputs
              </h4>
              <div className="space-y-2">
                {outputs.map((output, i) => (
                  <motion.div
                    key={output.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.5 + i * 0.05 }}
                    className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/5"
                  >
                    <output.icon size={16} className="text-[#06B6D4]" />
                    <span className="text-xs text-[#FAFAFA]">{output.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Common Objects */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 text-center"
          >
            <h4 className="text-sm font-medium text-white mb-4 font-['Outfit']">
              Common objects across all modules
            </h4>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "Systems", "Sources", "Teams", "Metrics", "Entities", 
                "Questions", "Decisions", "Documents", "Meetings", "Memory Objects"
              ].map((item) => (
                <span 
                  key={item}
                  className="px-3 py-1.5 rounded-full border border-white/10 text-xs text-[#A1A1AA] hover:border-[#00E5FF]/30 hover:text-white transition-colors"
                >
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default SharedDataSection;
